import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Trash2, Edit2 } from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";
import ThemedTable from "../../components/ThemedTable";
import ConfirmDialog from "../../components/ConfirmDialog";

const Offers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteOfferObj, setDeleteOfferObj] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    code: "",
    discount: "",
    minOrderAmount: "",
    maxDiscount: "",
    isActive: true,
    validFrom: "",
    validUpto: "",
  });

  /* ================= LOAD OFFERS ================= */
  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/offers/admin/all");
      setOffers(res.data || []);
    } catch (err) {
      console.error("Failed to load offers:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= HANDLE INPUT CHANGE ================= */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ================= SAVE OFFER ================= */
  const handleSave = async () => {
    try {
      setLoading(true);

      if (editingId) {
        await apiClient.put(`/offers/${editingId}`, formData);
      } else {
        await apiClient.post("/offers", formData);
      }

      await loadOffers();
      setShowForm(false);
      setEditingId(null);
      setFormData({
        title: "",
        description: "",
        code: "",
        discount: "",
        minOrderAmount: "",
        maxDiscount: "",
        isActive: true,
        validFrom: "",
        validUpto: "",
      });
      alert(
        editingId ? "Offer updated successfully" : "Offer created successfully",
      );
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save offer");
    } finally {
      setLoading(false);
    }
  };

  /* ================= EDIT OFFER ================= */
  const handleEdit = (offer) => {
    setFormData(offer);
    setEditingId(offer._id);
    setShowForm(true);
  };

  /* ================= DELETE OFFER ================= */
  const handleDeleteOffer = async () => {
    if (!deleteOfferObj) return;

    try {
      setLoading(true);
      await apiClient.delete(`/offers/${deleteOfferObj._id}`);
      await loadOffers();
      setDeleteOfferObj(null);
      alert("Offer deleted successfully");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete offer");
    } finally {
      setLoading(false);
    }
  };

  /* ================= CANCEL FORM ================= */
  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      title: "",
      description: "",
      code: "",
      discount: "",
      minOrderAmount: "",
      maxDiscount: "",
      isActive: true,
      validFrom: "",
      validUpto: "",
    });
  };

  return (
    <div style={{ marginTop: "0px" }}>
      {/* HEADER */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Manage Offers</h1>
          <p className="text-sm text-slate-500 mt-1">
            Create and manage promotional offers for your mobile application
          </p>
        </div>

        {/* ADD OFFER BUTTON - RIGHT SIDE */}
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 me-4 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-medium w-auto"
        >
          + Add New Offer
        </button>
      </div>

      {/* CONTENT */}
      <div className="flex-1 w-full">
        {/* MODAL BACKDROP */}
        {showForm && createPortal(
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[110] p-2">
            {/* MODAL */}
            <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
              {/* MODAL HEADER */}
              <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
                <h2 className="text-lg font-semibold">
                  {editingId ? "Edit Offer" : "Create New Offer"}
                </h2>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="text-xl text-white"
                >
                  ✕
                </button>
              </div>

              {/* MODAL BODY */}
              <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 p-6">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-slate-700">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="e.g., Welcome Offer"
                      className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-slate-700">
                      Code
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      placeholder="e.g., WELCOME10"
                      className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-slate-700">
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      name="discount"
                      value={formData.discount}
                      onChange={handleInputChange}
                      placeholder="e.g., 10"
                      className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-slate-700">
                      Min Order Amount (₹)
                    </label>
                    <input
                      type="number"
                      name="minOrderAmount"
                      value={formData.minOrderAmount}
                      onChange={handleInputChange}
                      placeholder="e.g., 500"
                      className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-slate-700">
                      Max Discount (₹)
                    </label>
                    <input
                      type="number"
                      name="maxDiscount"
                      value={formData.maxDiscount}
                      onChange={handleInputChange}
                      placeholder="e.g., 1000"
                      className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-slate-700">
                      Valid From
                    </label>
                    <input
                      type="date"
                      name="validFrom"
                      value={formData.validFrom}
                      onChange={handleInputChange}
                      className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                    />
                  </div>

                  <div className="flex flex-col gap-1 md:col-span-2">
                    <label className="text-xs font-medium text-slate-700">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="e.g., Get 10% OFF on your first purchase"
                      rows="3"
                      className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-slate-700">
                      Valid Upto
                    </label>
                    <input
                      type="date"
                      name="validUpto"
                      value={formData.validUpto}
                      onChange={handleInputChange}
                      className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                    />
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <label className="text-xs font-medium text-slate-700">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              {/* MODAL FOOTER */}
              <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-60"
                >
                  {loading ? "Saving..." : "Save Offer"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* OFFERS TABLE */}
        {offers.length > 0 ? (
          <div className="border border-slate-200 rounded-none bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <ThemedTable className="text-left border-collapse w-full">
                <thead>
                  <tr className="bg-white text-gray-700 border-b border-slate-200">
                    <th className="p-3 text-left whitespace-nowrap">Title</th>
                    <th className="p-3 text-left whitespace-nowrap">Code</th>
                    <th className="p-3 text-left whitespace-nowrap">
                      Discount
                    </th>
                    <th className="p-3 text-left whitespace-nowrap">
                      Min Amount
                    </th>
                    <th className="p-3 text-left whitespace-nowrap">Status</th>
                    <th className="p-3 text-center whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  {offers.map((offer) => (
                    <tr
                      key={offer._id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition"
                    >
                      <td className="p-3 text-sm font-medium text-slate-700">
                        {offer.title}
                      </td>
                      <td className="p-3 text-sm text-slate-600 font-mono">
                        {offer.code}
                      </td>
                      <td className="p-3 text-sm text-slate-600 font-semibold">
                        {offer.discount}%
                      </td>
                      <td className="p-3 text-sm text-slate-600">
                        ₹{offer.minOrderAmount || 0}
                      </td>
                      <td className="p-3 text-sm">
                        <span
                          className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-tight ${offer.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                        >
                          {offer.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEdit(offer)}
                            title="Edit Offer"
                            className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#172554] bg-[#172554] text-white hover:bg-white hover:text-[#172554] transition"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => setDeleteOfferObj(offer)}
                            title="Delete Offer"
                            className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-orange-500 bg-orange-500 text-white hover:bg-white hover:text-orange-500 transition"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ThemedTable>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-slate-500">
              No offers yet. Create your first offer to get started!
            </p>
          </div>
        )}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      <ConfirmDialog
        open={!!deleteOfferObj}
        title="Delete Offer"
        description={`Are you sure you want to delete ${deleteOfferObj?.title}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={loading}
        onCancel={() => setDeleteOfferObj(null)}
        onConfirm={handleDeleteOffer}
      />
    </div>
  );
};

export default Offers;
